import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { useAction, useMutation } from 'convex/react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { localDateStr } from '@/hooks/use-today-tasks'
import { downscaleImage } from '@/lib/image'
import { handleUsageLimit } from '@/lib/usage-limit'

// Uploads a photo or screenshot and reads it into capture items (Pro, one brain-dump use).
export function useReadCaptureImage() {
  const { i18n } = useTranslation()
  const uploadUrl = useMutation(api.mealPhoto.generateUploadUrl)
  const extract = useAction(api.voice.extractCaptureFromImage)
  return async (file: File) => {
    // Text in screenshots needs more pixels than a meal photo.
    const blob = await downscaleImage(file, 2048, 0.9)
    const res = await fetch(await uploadUrl(), {
      method: 'POST',
      headers: { 'Content-Type': blob.type || 'image/jpeg' },
      body: blob,
    })
    if (!res.ok) throw new Error(`upload ${res.status}`)
    const { storageId } = (await res.json()) as { storageId: Id<'_storage'> }
    return extract({ storageId, language: i18n.language, today: localDateStr() })
  }
}

export type PhotoTask = {
  key: number
  title: string
  selected: boolean
  dueDate: string | null
  time: string | null
  durationMinutes: number | null
}

// A photo of a list, read down to its tasks for a checklist review. Habits and goals in
// the photo are left out: these composers only add tasks.
export function usePhotoTasks() {
  const { t } = useTranslation()
  const readImage = useReadCaptureImage()
  const [reading, setReading] = useState(false)
  const [items, setItems] = useState<PhotoTask[] | null>(null)
  const runRef = useRef(0)

  async function read(file: File) {
    const run = ++runRef.current
    setReading(true)
    setItems(null)
    try {
      const { items: found } = await readImage(file)
      if (run !== runRef.current) return
      const tasks = found
        .filter((i) => (i.kind === 'task' || i.kind === 'event') && i.title.trim())
        .map((i, key) => ({
          key,
          title: i.title.trim(),
          selected: true,
          dueDate: i.dueDate,
          time: i.time,
          durationMinutes: i.durationMinutes,
        }))
      if (tasks.length) setItems(tasks)
      else toast(t('voice.imageNoItems'))
    } catch (err) {
      if (run === runRef.current && !handleUsageLimit(err)) toast.error(t('voice.imageError'))
    } finally {
      if (run === runRef.current) setReading(false)
    }
  }

  const patch = (key: number, p: Partial<PhotoTask>) =>
    setItems((prev) => prev && prev.map((i) => (i.key === key ? { ...i, ...p } : i)))

  return {
    reading,
    items,
    selected: (items ?? []).filter((i) => i.selected && i.title.trim()),
    read,
    toggle: (key: number) =>
      setItems(
        (prev) => prev && prev.map((i) => (i.key === key ? { ...i, selected: !i.selected } : i)),
      ),
    rename: (key: number, title: string) => patch(key, { title }),
    clear: () => {
      runRef.current++
      setReading(false)
      setItems(null)
    },
  }
}
